/**
 * QuickLookupModal — Instant customer/vendor balance & outstanding snapshot.
 *
 * Triggered by clicking a customer/vendor from search results.
 * Shows: summary cards → outstanding invoices (from recent txns) → contact → actions.
 * RAG colors on outstanding balance + aging summary bar.
 */

import { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import {
  X,
  AlertTriangle,
  Mail,
  Phone,
  ExternalLink,
  Receipt,
  FileText,
  Info,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { formatAmount, formatAmountShort, formatDate, formatRelativeTime } from '../../utils/formatters'
import { fetchQuickLookup } from '@/services/finance/financeService'
import type { QuickLookupResponse, QuickLookupAging } from '../../types'

interface QuickLookupModalProps {
  open: boolean
  subledgerId: string | null
  onClose: () => void
}

function getOutstandingRAG(aging: QuickLookupAging): 'green' | 'amber' | 'red' {
  if (aging.days61to90 > 0 || aging.over90 > 0) return 'red'
  if (aging.days1to30 > 0 || aging.days31to60 > 0) return 'amber'
  return 'green'
}

const RAG_LABELS = {
  green: 'All clear',
  amber: 'Some overdue',
  red: 'Significant overdue',
}

const RAG_COLORS = {
  green: 'text-green-600 dark:text-green-400',
  amber: 'text-yellow-600 dark:text-yellow-400',
  red: 'text-red-500',
}

export function QuickLookupModal({ open, subledgerId, onClose }: QuickLookupModalProps) {
  const navigate = useNavigate()
  const [data, setData] = useState<QuickLookupResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadData = useCallback(async (id: string) => {
    setIsLoading(true)
    setError(null)
    try {
      const result = await fetchQuickLookup(id)
      setData(result)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load lookup')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (open && subledgerId) {
      loadData(subledgerId)
    }
    return () => {
      if (!open) setData(null)
    }
  }, [open, subledgerId, loadData])

  // Esc to close
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, onClose])

  const handleNavigateAndClose = useCallback(
    (path: string) => {
      onClose()
      navigate(path)
    },
    [navigate, onClose],
  )

  if (!open) return null

  const rag = data ? getOutstandingRAG(data.agingSummary) : 'green'
  const isCustomer = data?.subledger.type.code === 'CUSTOMER'
  const agingTotal = data
    ? data.agingSummary.current +
      data.agingSummary.days1to30 +
      data.agingSummary.days31to60 +
      data.agingSummary.days61to90 +
      data.agingSummary.over90
    : 0

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-50 bg-black/40" onClick={onClose} />

      {/* Modal */}
      <div className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 w-[600px] max-h-[85vh] overflow-y-auto rounded-xl border bg-card bg-card shadow-2xl">
        {/* Loading */}
        {isLoading && (
          <div className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <Skeleton className="h-6 w-48" />
              <Button variant="ghost" size="icon" className="size-8" onClick={onClose}>
                <X className="size-4" />
              </Button>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="rounded-lg border bg-card p-3 space-y-2">
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="h-5 w-24" />
                </div>
              ))}
            </div>
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-8 w-full" />
              ))}
            </div>
          </div>
        )}

        {/* Error */}
        {error && !data && !isLoading && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Quick Lookup</h2>
              <Button variant="ghost" size="icon" className="size-8" onClick={onClose}>
                <X className="size-4" />
              </Button>
            </div>
            <div className="flex flex-col items-center gap-3 py-8">
              <AlertTriangle className="size-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">{error}</p>
            </div>
          </div>
        )}

        {/* Data */}
        {data && !isLoading && (
          <div className="p-6 space-y-5">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">{data.subledger.name}</h2>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs text-muted-foreground">{data.subledger.code}</span>
                  <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-muted text-muted-foreground">
                    {data.subledger.type.name}
                  </span>
                </div>
              </div>
              <Button variant="ghost" size="icon" className="size-8" onClick={onClose}>
                <X className="size-4" />
              </Button>
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg border bg-card p-3">
                <div className="flex items-center gap-1 mb-1">
                  <p className="text-xs font-medium text-muted-foreground">Outstanding</p>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="size-3 text-muted-foreground/50 hover:text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[200px]">
                      Total unpaid invoice amounts (excludes unallocated receipts)
                    </TooltipContent>
                  </Tooltip>
                </div>
                <p className="text-lg font-semibold tabular-nums">
                  {formatAmount(data.outstandingBalance)}
                </p>
                <p className={cn('text-xs font-medium', RAG_COLORS[rag])}>
                  {RAG_LABELS[rag]}
                </p>
              </div>
              <div className="rounded-lg border bg-card p-3">
                <p className="text-xs font-medium text-muted-foreground mb-1">Transactions</p>
                <p className="text-lg font-semibold tabular-nums">{data.totalTransactions}</p>
                <p className="text-xs text-muted-foreground">total</p>
              </div>
              <div className="rounded-lg border bg-card p-3">
                <p className="text-xs font-medium text-muted-foreground mb-1">Last Activity</p>
                {data.recentTransactions.length > 0 ? (
                  <>
                    <p className="text-lg font-semibold tabular-nums">
                      {formatDate(data.recentTransactions[0].voucherDate)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatRelativeTime(data.recentTransactions[0].voucherDate)}
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">No activity</p>
                )}
              </div>
            </div>

            {/* Credit Utilization — only shown when credit limit is configured */}
            {data.creditLimit != null && data.creditLimit > 0 && (
              <div className="rounded-lg border bg-card p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-muted-foreground">Credit Utilization</p>
                  <span className="text-xs text-muted-foreground">
                    Limit: {formatAmount(data.creditLimit)}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="flex h-2 rounded-full overflow-hidden bg-muted">
                      <div
                        className={cn(
                          'transition-all rounded-full',
                          data.creditAvailable != null && data.creditAvailable < 0
                            ? 'bg-red-500'
                            : (data.outstandingBalance / data.creditLimit) > 0.8
                              ? 'bg-amber-500'
                              : 'bg-green-500'
                        )}
                        style={{
                          width: `${Math.min((data.outstandingBalance / data.creditLimit) * 100, 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                  <span
                    className={cn(
                      'text-sm font-semibold tabular-nums whitespace-nowrap',
                      data.creditAvailable != null && data.creditAvailable < 0
                        ? 'text-red-500'
                        : (data.outstandingBalance / data.creditLimit) > 0.8
                          ? 'text-amber-600'
                          : 'text-green-600'
                    )}
                  >
                    {formatAmount(data.creditAvailable ?? 0)} available
                  </span>
                </div>
              </div>
            )}

            {/* Aging summary bar */}
            {agingTotal > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">Aging Breakdown</p>
                <div className="flex h-3 rounded-full overflow-hidden bg-muted">
                  <AgingSegment amount={data.agingSummary.current} total={agingTotal} color="bg-green-500" />
                  <AgingSegment amount={data.agingSummary.days1to30} total={agingTotal} color="bg-yellow-500" />
                  <AgingSegment amount={data.agingSummary.days31to60} total={agingTotal} color="bg-orange-500" />
                  <AgingSegment amount={data.agingSummary.days61to90} total={agingTotal} color="bg-red-400" />
                  <AgingSegment amount={data.agingSummary.over90} total={agingTotal} color="bg-red-600" />
                </div>
                <div className="flex justify-between mt-1.5 text-xs text-muted-foreground">
                  <span>Current: {formatAmountShort(data.agingSummary.current)}</span>
                  <span>1-30: {formatAmountShort(data.agingSummary.days1to30)}</span>
                  <span>31-60: {formatAmountShort(data.agingSummary.days31to60)}</span>
                  <span>61-90: {formatAmountShort(data.agingSummary.days61to90)}</span>
                  <span>90+: {formatAmountShort(data.agingSummary.over90)}</span>
                </div>
              </div>
            )}

            {/* Recent transactions */}
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">
                Recent Transactions ({data.recentTransactions.length})
              </p>
              {data.recentTransactions.length > 0 ? (
                <div className="rounded-lg border bg-card divide-y">
                  {data.recentTransactions.map((txn) => (
                    <button
                      key={txn.voucherId}
                      onClick={() =>
                        handleNavigateAndClose(`/finance/transactions?selected=${txn.voucherId}`)
                      }
                      className="flex w-full items-center justify-between px-3 py-2 hover:bg-muted/30 transition-colors text-left text-sm"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-xs text-muted-foreground tabular-nums w-[72px] shrink-0">
                          {formatDate(txn.voucherDate)}
                        </span>
                        <span className="text-primary font-medium truncate">{txn.voucherNo}</span>
                        <span className="text-xs text-muted-foreground">{txn.type.replace('_', ' ')}</span>
                      </div>
                      <div className="flex items-center gap-3 shrink-0 ml-3">
                        {txn.debit > 0 && (
                          <span className="text-xs tabular-nums text-red-500">
                            Dr {formatAmountShort(txn.debit)}
                          </span>
                        )}
                        {txn.credit > 0 && (
                          <span className="text-xs tabular-nums text-green-600">
                            Cr {formatAmountShort(txn.credit)}
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="rounded-lg border bg-card px-4 py-4 text-center">
                  <p className="text-sm text-muted-foreground">No recent transactions</p>
                </div>
              )}
            </div>

            {/* Contact */}
            {data.subledger.contact && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">Contact</p>
                <div className="flex items-center gap-4 text-sm">
                  {data.subledger.contact.email && (
                    <a
                      href={`mailto:${data.subledger.contact.email}`}
                      className="flex items-center gap-1.5 text-primary hover:underline"
                    >
                      <Mail className="size-3.5" />
                      {data.subledger.contact.email}
                    </a>
                  )}
                  {data.subledger.contact.phone && (
                    <a
                      href={`tel:${data.subledger.contact.phone}`}
                      className="flex items-center gap-1.5 text-primary hover:underline"
                    >
                      <Phone className="size-3.5" />
                      {data.subledger.contact.phone}
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-2 pt-2 border-t">
              <Button
                variant="outline"
                size="sm"
                className="text-xs gap-1.5"
                onClick={() =>
                  handleNavigateAndClose(
                    isCustomer
                      ? `/finance/reports/statement/customer/${subledgerId}`
                      : `/finance/reports/statement/vendor/${subledgerId}`,
                  )
                }
              >
                <FileText className="size-3.5" />
                View Statement
              </Button>
              {isCustomer && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs gap-1.5"
                  onClick={() =>
                    handleNavigateAndClose(
                      `/finance/transactions?action=new-receipt&subledgerId=${subledgerId}`,
                    )
                  }
                >
                  <Receipt className="size-3.5" />
                  Create Receipt
                </Button>
              )}
              {!isCustomer && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs gap-1.5"
                  onClick={() =>
                    handleNavigateAndClose(
                      `/finance/transactions?action=new-payment&subledgerId=${subledgerId}`,
                    )
                  }
                >
                  <Receipt className="size-3.5" />
                  Create Payment
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="text-xs gap-1.5 ml-auto"
                onClick={() =>
                  handleNavigateAndClose(`/finance/masters/parties?selected=${subledgerId}`)
                }
              >
                Full Profile
                <ExternalLink className="size-3" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </>,
    document.body,
  )
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function AgingSegment({
  amount,
  total,
  color,
}: {
  amount: number
  total: number
  color: string
}) {
  if (amount <= 0 || total <= 0) return null
  const pct = (amount / total) * 100
  return <div className={cn(color, 'transition-all')} style={{ width: `${pct}%` }} />
}
